import React from "react";
import { format } from "date-fns";
import { Bell, MessageSquare, Reply, Check } from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import { motion, AnimatePresence } from "framer-motion";

export default function InlineNotificationCenter({
    notifications = [],
    onMarkAsRead,
    onMarkAllAsRead
}) {
    const unreadCount = notifications.filter(n => !n.read_at).length;

    const getNotificationIcon = (type) => {
        switch (type) {
            case "personal":
                return <Bell className="w-5 h-5 text-blue-500" />;
            case "community_digest":
                return <MessageSquare className="w-5 h-5 text-purple-500" />;
            case "reply":
                return <Reply className="w-5 h-5 text-emerald-500" />;
            default:
                return <Bell className="w-5 h-5 text-slate-400" />;
        }
    };

    return (
        <Card className="border-slate-200 shadow-lg max-w-md w-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <div className="flex items-center gap-2">
                    <Bell className="w-5 h-5 text-slate-600" />
                    <h3 className="font-semibold text-slate-900">Notifications</h3>
                    {unreadCount > 0 && (
                        <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                            {unreadCount}
                        </span>
                    )}
                </div>
                {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={onMarkAllAsRead}>
                        <Check className="w-4 h-4 mr-1" />
                        Mark all read
                    </Button>
                )}
            </div>

            {/* Notification list */}
            <ScrollArea className="h-96">
                <AnimatePresence>
                    {notifications.length === 0 ? (
                        <div className="p-8 text-center">
                            <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                            <p className="text-slate-500 text-sm">No notifications yet</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-slate-100">
                            {notifications.map((notification) => (
                                <motion.div
                                    key={notification.id}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: 20 }}
                                    className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${!notification.read_at ? "bg-blue-50/50" : ""
                                        }`}
                                    onClick={() => !notification.read_at && onMarkAsRead(notification.id)}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="flex-shrink-0 mt-0.5">
                                            {getNotificationIcon(notification.notification_type)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className={`text-sm leading-relaxed ${!notification.read_at ? "font-medium text-slate-900" : "text-slate-600"
                                                }`}>
                                                {notification.notification_text}
                                            </p>
                                            {notification.community_count > 0 && (
                                                <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
                                                    <span>{notification.community_count} community</span>
                                                    {notification.admin_count > 0 && (
                                                        <>
                                                            <span>·</span>
                                                            <span>{notification.admin_count} admin</span>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                            <span className="text-xs text-slate-400 mt-1 block">
                                                {format(new Date(notification.created_date), "MMM d, h:mm a")}
                                            </span>
                                        </div>
                                        {!notification.read_at && (
                                            <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2" />
                                        )}
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    )}
                </AnimatePresence>
            </ScrollArea>
        </Card>
    );
}
